import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { endFlow, flowStep, mark, measure } from '@/lib/perf';

/**
 * AuthGuard — redirects to /login if not authenticated or not practice role.
 * Preserves return URL for redirect after login.
 */
export function AuthGuard({ children }: { children?: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user || user.role !== 'practice') return;

    mark('login_first_data_fetch_done');
    flowStep('login', 'first_data_fetch_done');

    requestAnimationFrame(() => {
      mark('login_ui_ready');
      measure('login_time_to_ui_ready_ms', 'login_click', 'login_ui_ready');
      flowStep('login', 'ui_ready', { route: location.pathname });
      endFlow('login', 'ok', { route: location.pathname });
    });
  }, [loading, user, location.pathname]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="text-sm text-neutral-500">Wird geladen…</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'practice') {
    flowStep('login', 'auth_guard_redirect_login', { route: location.pathname });
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
